import type { Maybe } from '@adornis/base/utilTypes';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar';
import { stateful, useState } from '@adornis/functional-lit/stateful';
import { html, nothing } from 'lit';
import { LASUser } from '../../../db/las-user';
import { EmptyAvatar, UserAvatar } from './UserAvatar';

export const UserPicker = stateful(
  ({
    label,
    assignedUserId,
    availableUsers,
    isCreator,
    currentUser,
    onUserAssigned,
    onUserRemoved,
    isLeftAligned = true,
    canEdit = true,
  }: {
    label: string;
    assignedUserId?: Maybe<string>;
    availableUsers: LASUser[];
    isCreator: boolean;
    currentUser: LASUser;
    onUserAssigned: (userId: string) => Promise<void>;
    onUserRemoved: () => Promise<void>;
    isLeftAligned?: boolean;
    canEdit: boolean;
  }) => {
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const assignedUser = assignedUserId ? availableUsers.find(u => u._id === assignedUserId) : null;

    if (!assignedUser && !canEdit) return nothing;

    return html`
      <div class="flex flex-col gap-1 relative">
        <div class="flex items-center">
          <div @click=${() => setDropdownOpen(!dropdownOpen)} class="cursor-pointer">
            ${assignedUser ? UserAvatar.render({ user: assignedUser }) : EmptyAvatar(`${label} zuweisen`)}
          </div>

          ${assignedUser && canEdit
            ? html`
                <button
                  class="btn btn-ghost btn-xs ml-2"
                  @click=${async (e: Event) => {
                    e.stopPropagation();
                    await onUserRemoved();
                    XSnackbar.show(`${label} entfernt`);
                  }}
                >
                  <d-icon solid>x</d-icon>
                </button>
              `
            : nothing}
        </div>

        ${dropdownOpen && canEdit
          ? html`
              <div
                class="absolute top-full ${isLeftAligned
                  ? 'left-0'
                  : 'right-0'} mt-1 z-10 bg-base-100 shadow-lg rounded-lg p-2 w-48 max-h-60 overflow-y-auto border border-base-300"
              >
                <div class="flex flex-col gap-2">
                  ${isCreator
                    ? html`
                        ${availableUsers.map(
                          user => html`
                            <div
                              class="flex items-center gap-2 p-1 hover:bg-base-200 rounded cursor-pointer"
                              @click=${async () => {
                                await onUserAssigned(user._id);
                                XSnackbar.show(`${user.email} als ${label} zugewiesen`);
                                setDropdownOpen(false);
                              }}
                            >
                              ${UserAvatar.render({ user })}
                              <span class="text-sm truncate">${currentUser._id === user._id ? 'Ich' : user.email}</span>
                            </div>
                          `,
                        )}
                      `
                    : html`
                        <div
                          class="flex items-center gap-2 p-1 hover:bg-base-200 rounded cursor-pointer"
                          @click=${async () => {
                            await onUserAssigned(currentUser._id);
                            XSnackbar.show(`Sie wurden als ${label} zugewiesen`);
                            setDropdownOpen(false);
                          }}
                        >
                          ${UserAvatar.render({ user: currentUser })}
                          <span class="text-sm truncate"> Ich </span>
                        </div>
                      `}
                </div>
              </div>
            `
          : nothing}
      </div>
    `;
  },
);
